// src/pages/CookiePage.jsx
import * as React from 'react';
import {
  Box, Container, Typography, Paper, Stack, Chip, Switch, Button, Divider, Alert,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignIcon from '@mui/icons-material/Campaign';
import SpeedIcon from '@mui/icons-material/Speed';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import cookieI18n from '../i18n/pages/cookiepage.i18n';

const IS_COLLECT_HOST = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('collect.');
const BLUE = IS_COLLECT_HOST ? '#197348' : '#0466c8';
const BLUE_BG = IS_COLLECT_HOST ? 'rgba(25,115,72,0.09)' : 'rgba(11,95,255,0.08)';
const BLUE_HOVER = IS_COLLECT_HOST ? '#105c37' : '#004890';

const COOKIE_KEY = 'cookie_prefs_v1';

/** ⚙️ Catégories (les libellés sont injectés via i18n au rendu) */
const COOKIE_CATEGORIES = [
  { key: 'essential',  icon: <SecurityIcon />,  locked: true,  default: true },
  // ✅ Option B: analytics activé par défaut
  // REVERT: remets default:false pour revenir au comportement d’origine
  // { key: 'analytics',  icon: <BarChartIcon />,  locked: false, default: false },
  { key: 'analytics',  icon: <BarChartIcon />,  locked: false, default: true },
  { key: 'performance',icon: <SpeedIcon />,     locked: false, default: false },
  { key: 'marketing',  icon: <CampaignIcon />,  locked: false, default: false },
];

/** Helpers localStorage */
function loadPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(COOKIE_KEY) || 'null');
    if (saved && typeof saved === 'object') return saved;
  } catch {}
  const initial = {};
  COOKIE_CATEGORIES.forEach(c => { initial[c.key] = !!c.default; });
  return initial;
}
function savePrefs(prefs) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs || {}));
}

/** Injection GA4 si Analytics accepté */
function ensureGAInitialized(measurementId) {
  if (!measurementId) return;
  if (window.__gaLoaded) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  window.__gaLoaded = true;
}

/** Met à jour le consentement côté gtag si présent */
function updateGtagConsent(prefs) {
  if (!window.gtag) return;
  const analyticsGranted = !!prefs.analytics;
  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
  });
}

export default function CookiePage() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('cookie', cookieI18n); }, [register]);

  const [prefs, setPrefs] = React.useState(loadPrefs);
  const [savedStamp, setSavedStamp] = React.useState(null);
  const [info, setInfo] = React.useState('');

  // 👉 ID GA4 (ex: G-XXXXXXX)
  const GA_MEASUREMENT_ID = import.meta?.env?.VITE_GA_MEASUREMENT_ID || '';

  // ⬇️ Au premier rendu, si analytics est ON par défaut, on injecte GA et on pousse le consentement courant.
  // (Cette page n’est pas nécessaire au démarrage de GA; c’est surtout pour cohérence si on arrive directement ici.)
  React.useEffect(() => {
    if (prefs.analytics && GA_MEASUREMENT_ID) {
      ensureGAInitialized(GA_MEASUREMENT_ID);
      updateGtagConsent(prefs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOne = (key, val) => {
    setPrefs(p => ({ ...p, [key]: val }));
  };

  const acceptAll = () => {
    const next = {};
    COOKIE_CATEGORIES.forEach(c => { next[c.key] = true; });
    setPrefs(next);
    savePrefs(next);
    if (GA_MEASUREMENT_ID) ensureGAInitialized(GA_MEASUREMENT_ID);
    updateGtagConsent(next);
    setSavedStamp(Date.now());
    setInfo(t('cookie.toast.allAccepted'));
  };

  const rejectNonEssential = () => {
    const next = {};
    COOKIE_CATEGORIES.forEach(c => { next[c.key] = !!c.locked; });
    setPrefs(next);
    savePrefs(next);
    updateGtagConsent(next);
    setSavedStamp(Date.now());
    setInfo(t('cookie.toast.onlyEssential'));
  };

  const save = () => {
    const next = { ...prefs, essential: true };
    setPrefs(next);
    savePrefs(next);
    if (next.analytics && GA_MEASUREMENT_ID) ensureGAInitialized(GA_MEASUREMENT_ID);
    updateGtagConsent(next);
    setSavedStamp(Date.now());
    setInfo(t('cookie.toast.saved'));
  };

  return (
    <>
      {IS_COLLECT_HOST ? <Box component="header" sx={{ bgcolor: '#fff', borderBottom: '1px solid #d9e7de' }}><Container maxWidth="lg"><Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 72 }}><Typography component="a" href="/" sx={{ color: BLUE, textDecoration: 'none', fontSize: 22, fontWeight: 950 }}>123back <Box component="span" sx={{ color: '#123a2a', fontWeight: 700 }}>Collect</Box></Typography><Button href="/" sx={{ color: BLUE, textTransform: 'none', fontWeight: 800 }}>Retour à l’accueil</Button></Stack></Container></Box> : <Navbar />}

      {/* HERO */}
      <Box sx={{ bgcolor: BLUE, color: '#fff', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: 26, md: 36 }, lineHeight: 1.15 }}>
            {t('cookie.hero.title')}
          </Typography>
          <Typography sx={{ mt: 1, opacity: 0.95, fontSize: { xs: 15.5, md: 17 } }}>
            {t('cookie.hero.desc')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
            <Chip size="small" label={t('cookie.hero.badges.essential')}  sx={{ bgcolor: '#fff', color: BLUE, fontWeight: 700 }} />
            <Chip size="small" label={t('cookie.hero.badges.analytics')}  sx={{ bgcolor: '#fff', color: BLUE, fontWeight: 700 }} />
            <Chip size="small" label={t('cookie.hero.badges.performance')} sx={{ bgcolor: '#fff', color: BLUE, fontWeight: 700 }} />
            <Chip size="small" label={t('cookie.hero.badges.marketing')}  sx={{ bgcolor: '#fff', color: BLUE, fontWeight: 700 }} />
          </Stack>
        </Container>
      </Box>

      {/* CONTENT */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          {info && (
            <Alert
              severity="success"
              onClose={() => setInfo('')}
              sx={{ mb: 2, borderRadius: 2, bgcolor: BLUE_BG }}
            >
              {info}
            </Alert>
          )}

          {/* Résumé & actions rapides */}
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, borderColor: '#e8efe9', mb: 2.5 }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Typography sx={{ color: 'text.secondary' }}>
                {t('cookie.summary.hint')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  onClick={rejectNonEssential}
                  sx={{
                    textTransform: 'none', borderRadius: 2,
                    borderColor: BLUE, color: BLUE,
                    '&:hover': { borderColor: BLUE_HOVER, color: BLUE_HOVER }
                  }}
                >
                  {t('cookie.summary.reject')}
                </Button>
                <Button
                  variant="contained"
                  onClick={acceptAll}
                  sx={{
                    textTransform: 'none', borderRadius: 2,
                    bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER }
                  }}
                >
                  {t('cookie.summary.accept')}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Catégories */}
          <Stack spacing={1.5}>
            {COOKIE_CATEGORIES.map(cat => {
              const enabled = !!prefs[cat.key];
              const label = t(`cookie.categories.${cat.key}.label`);
              const description = t(`cookie.categories.${cat.key}.desc`);
              return (
                <Paper key={cat.key} variant="outlined" sx={{ borderRadius: 3, borderColor: '#e8efe9' }}>
                  <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ width: '100%', pr: 1 }}
                      >
                        <Box
                          sx={{
                            bgcolor: BLUE_BG,
                            color: BLUE,
                            borderRadius: 2,
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {cat.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800 }}>{label}</Typography>
                          <Typography sx={{ color: 'text.secondary', fontSize: 13, display: { xs: 'none', sm: 'block' } }}>
                            {description}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {enabled ? t('cookie.state.on') : t('cookie.state.off')}
                          </Typography>
                          <Switch
                            edge="end"
                            color={IS_COLLECT_HOST ? 'success' : 'primary'}
                            disabled={!!cat.locked}
                            checked={enabled}
                            onChange={(_, v) => setOne(cat.key, v)}
                          />
                        </Stack>
                      </Stack>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Divider sx={{ mb: 1.25 }} />
                      <Typography sx={{ color: 'text.secondary' }}>{description}</Typography>

                      {/* Mentions spécifiques analytics */}
                      {cat.key === 'analytics' && (
                        <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                          {t('cookie.analytics.note')}{' '}
                          <code>{GA_MEASUREMENT_ID || t('cookie.analytics.notConfigured')}</code>.
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              );
            })}
          </Stack>

          {/* CTA enregistrer */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={rejectNonEssential}
              sx={{
                textTransform: 'none', borderRadius: 2,
                borderColor: BLUE, color: BLUE,
                '&:hover': { borderColor: BLUE_HOVER, color: BLUE_HOVER }
              }}
            >
              {t('cookie.cta.rejectNonEssential')}
            </Button>
            <Button
              variant="contained"
              onClick={save}
              sx={{
                textTransform: 'none', borderRadius: 2,
                bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER }
              }}
            >
              {t('cookie.cta.save')}
            </Button>
          </Stack>

          {savedStamp && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.25 }}>
              {t('cookie.savedAt')} {new Date(savedStamp).toLocaleString()}
            </Typography>
          )}

          {/* Mentions */}
          <Paper variant="outlined" sx={{ mt: 3, p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>{t('cookie.more.title')}</Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {t('cookie.more.text')}{' '}
              <a href={IS_COLLECT_HOST ? '/' : '/services'} style={{ color: BLUE, textDecoration: 'underline' }}>
                {t('cookie.more.link')}
              </a>.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {IS_COLLECT_HOST ? <Box component="footer" sx={{ bgcolor: '#0e2e22', color: '#c8ddd0', py: 3, textAlign: 'center' }}><Typography fontWeight={800}>123back Collect · S&amp;T France · contact@123back.eu</Typography></Box> : <Footer />}
    </>
  );
}
